import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calculator, TrendingUp, Zap, ChevronRight, Star, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { fmtAmt } from '@/utils/format';
import PageLoader from '@/components/PageLoader';

// ─── Live Insights Trends ─────────────────────────────────────────────────────

function LiveInsightsTrends({ snap }) {
  const { token } = useAuth();
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    setLoading(true);
    Promise.allSettled([
      axios.get(`${API}/budget-summary`, { headers: h }),
      axios.get(`${API}/transactions`, { headers: h, params: { month, year } }),
      axios.get(`${API}/financial-score`, { headers: h }),
    ]).then(([bsRes, txnRes, fsRes]) => {
      if (bsRes.status === 'fulfilled') setBudgetSummary(bsRes.value.data);
      if (txnRes.status === 'fulfilled') setTransactions(txnRes.value.data || []);
      if (fsRes.status === 'fulfilled') setMonthlyData(fsRes.value.data?.monthly_data || []);
      setLoading(false);
    });
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-2">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-stone-400">Crunching your live data…</p>
        </div>
      </div>
    );
  }

  const bs = budgetSummary || {};
  const totalIncome = bs.total_income || 0;
  const totalSpent = bs.total_spent || 0;
  const daysElapsed = bs.days_elapsed || 0;
  const daysRemaining = bs.days_remaining || 0;
  const totalDays = bs.total_days_in_cycle || 30;
  const paceState = bs.pace_state || 'unknown';
  const safeToSpendToday = bs.safe_to_spend_today || 0;
  const categories = bs.categories || [];

  // today's spend
  const todayStr = new Date().toISOString().slice(0, 10);
  const todaySpend = transactions
    .filter(t => t.date === todayStr && t.type === 'expense')
    .reduce((s, t) => s + (t.amount || 0), 0);

  const dailyAvg = totalSpent / Math.max(1, daysElapsed);
  const forecast = totalIncome - totalSpent - dailyAvg * daysRemaining;
  const progressPct = Math.min(100, Math.round((daysElapsed / Math.max(1, totalDays)) * 100));

  // pace color
  const paceColor = paceState === 'on_track' ? 'text-emerald-400'
    : paceState === 'watch' ? 'text-amber-400'
    : paceState === 'over' ? 'text-red-400'
    : 'text-stone-400';
  const paceLabel = paceState === 'on_track' ? '🟢 On track'
    : paceState === 'watch' ? '🟡 Watch pace'
    : paceState === 'over' ? '🔴 Overspending'
    : '—';

  // last month savings for comparison
  const sortedMonthly = [...monthlyData].sort((a, b) => b.month.localeCompare(a.month));
  const lastMonthData = sortedMonthly[1] || null;
  const lastMonthSavings = lastMonthData
    ? Math.max(0, (lastMonthData.income || 0) - (lastMonthData.expense || 0))
    : null;
  const forecastDiff = lastMonthSavings !== null ? forecast - lastMonthSavings : null;

  // ── Section 2: Category Speedometers ────────────────────────────────────────
  const speedoCats = categories
    .filter(c => c.allocated > 0 && c.spent > 0)
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 6);

  function getCatBarColor(pct) {
    if (pct > 100) return 'bg-red-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  }

  function getCatRunway(c) {
    const over = c.spent - c.allocated;
    if (over > 0) return { label: `₹${fmtAmt(Math.round(over))} over ⚠`, danger: true };
    const headroom = c.allocated - c.spent;
    const dailyRate = c.spent / Math.max(1, daysElapsed);
    const daysToEmpty = dailyRate > 0 ? Math.floor(headroom / dailyRate) : 999;
    if (daysToEmpty < daysRemaining && daysToEmpty < 999) {
      return { label: `Runs out in ${daysToEmpty} day${daysToEmpty !== 1 ? 's' : ''} ⚠`, danger: true };
    }
    return { label: `₹${fmtAmt(Math.round(headroom))} headroom ✓`, danger: false };
  }

  // ── Section 3: Pattern Reveals ───────────────────────────────────────────────
  const expenseTxns = transactions.filter(t => t.type === 'expense');
  const hasEnoughData = expenseTxns.length >= 8;

  // Weekend effect
  const byDay = Array(7).fill(null).map(() => ({ total: 0, count: 0 }));
  expenseTxns.forEach(t => {
    const day = new Date(t.date).getDay();
    if (day >= 0 && day <= 6) {
      byDay[day].total += t.amount || 0;
      byDay[day].count++;
    }
  });
  const weekdayAvg = [1, 2, 3, 4, 5]
    .map(d => byDay[d].total / Math.max(1, byDay[d].count))
    .reduce((a, b) => a + b, 0) / 5;
  const weekendAvg = [0, 6]
    .map(d => byDay[d].total / Math.max(1, byDay[d].count))
    .reduce((a, b) => a + b, 0) / 2;
  const weekendRatio = weekendAvg / Math.max(1, weekdayAvg);
  const showWeekend = hasEnoughData && weekendRatio > 1.4;

  const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayAvgs = byDay.map(d => d.total / Math.max(1, d.count));
  const maxDayAvg = Math.max(...dayAvgs, 1);

  // Payday surge
  const earlySpend = expenseTxns
    .filter(t => new Date(t.date).getDate() <= 5)
    .reduce((s, t) => s + (t.amount || 0), 0);
  const totalExpenseAmt = expenseTxns.reduce((s, t) => s + (t.amount || 0), 0);
  const surgePct = (earlySpend / Math.max(1, totalExpenseAmt)) * 100;
  const showPaydaySurge = hasEnoughData && surgePct > 30;

  // The Translator
  const catTotals = {};
  expenseTxns.forEach(t => {
    const name = t.category_name || 'Other';
    catTotals[name] = (catTotals[name] || 0) + (t.amount || 0);
  });
  const topCatEntry = Object.entries(catTotals).sort((a, b) => b[1] - a[1])[0];
  const topCatName = topCatEntry?.[0] || '';
  const topSpend = topCatEntry?.[1] || 0;
  const annualIncome = (snap?.income || 0) * 12;
  const salaryHour = annualIncome / 2080;
  const equivalences = [];
  if (topSpend >= 100) equivalences.push(`${Math.round(topSpend / 100)} Swiggy orders`);
  if (topSpend >= 649) equivalences.push(`${(topSpend / 649).toFixed(1)} months of Netflix`);
  if (salaryHour > 0 && topSpend / salaryHour >= 1) equivalences.push(`${Math.round(topSpend / salaryHour)} hours of your salary`);
  const showTranslator = hasEnoughData && topCatName && equivalences.length >= 2;

  // ── Section 4: Spend Personality ────────────────────────────────────────────
  const badges = [];
  const diningSpend = categories.find(
    c => c.name?.toLowerCase().includes('food') || c.name?.toLowerCase().includes('dining')
  )?.spent || 0;
  if (diningSpend / Math.max(1, totalSpent) > 0.30) {
    badges.push({ icon: '🍔', name: 'The Foodie', desc: 'Food is your love language' });
  }
  const savingsRateVal = totalIncome > 0 ? (totalIncome - totalSpent) / totalIncome : 0;
  if (totalIncome > 0 && savingsRateVal > 0.20) {
    badges.push({ icon: '💎', name: 'The Saver', desc: 'Future you says thanks' });
  }
  const emiTotal = bs.total_emi || snap?.emiTotal || 0;
  if (totalIncome > 0 && emiTotal / Math.max(1, totalIncome) > 0.30) {
    badges.push({ icon: '💪', name: 'The EMI Fighter', desc: 'Chipping away at debt' });
  }
  const displayBadges = badges.slice(0, 3);

  return (
    <div className="space-y-4">

      {/* ── Section 1: Today's Pulse Card ── */}
      <div className="bg-stone-900 text-white rounded-2xl p-6 space-y-4">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold tracking-widest text-stone-400 uppercase">LIVE</span>
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <span className="text-xs text-stone-400">Day {daysElapsed} of {totalDays}</span>
        </div>

        {/* Progress bar */}
        <div>
          <div className="w-full h-2 bg-stone-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progressPct}%`,
                backgroundColor: paceState === 'on_track' ? '#10b981'
                  : paceState === 'watch' ? '#f59e0b'
                  : paceState === 'over' ? '#ef4444'
                  : '#6b7280',
              }}
            />
          </div>
          <p className="text-[11px] text-stone-500 mt-1">{progressPct}% of cycle used</p>
        </div>

        {/* Spend grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-extrabold">₹{fmtAmt(Math.round(todaySpend))}</p>
            <p className="text-xs text-stone-400">today</p>
          </div>
          <div>
            <p className="text-2xl font-extrabold text-orange-400">₹{fmtAmt(Math.round(safeToSpendToday))}</p>
            <p className="text-xs text-stone-400">safe today</p>
          </div>
        </div>

        {/* Avg + pace */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-stone-400">Avg ₹{fmtAmt(Math.round(dailyAvg))}/day</p>
          <p className={`text-xs font-semibold ${paceColor}`}>{paceLabel}</p>
        </div>

        {/* Forecast */}
        {totalIncome > 0 && (
          <div className={`rounded-xl px-4 py-2.5 ${forecast >= 0 ? 'bg-emerald-900/40 border border-emerald-700/40' : 'bg-red-900/40 border border-red-700/40'}`}>
            {forecast >= 0 ? (
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-emerald-300">
                  → Saving ₹{fmtAmt(Math.round(forecast))} by month-end
                </p>
                {forecastDiff !== null && (
                  <p className={`text-xs font-semibold ${forecastDiff >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {forecastDiff >= 0 ? '↑' : '↓'} {forecastDiff >= 0 ? 'better' : 'worse'} than last month
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm font-bold text-red-300">
                → On track to overspend by ₹{fmtAmt(Math.round(Math.abs(forecast)))}
              </p>
            )}
          </div>
        )}
        {totalIncome === 0 && (
          <div className="bg-stone-800 rounded-xl px-4 py-2.5">
            <p className="text-xs text-stone-400">
              Set your income to activate forecast.{' '}
              <Link to="/chat" className="text-orange-400 underline">Add via Chanakya →</Link>
            </p>
          </div>
        )}
      </div>

      {/* ── Section 2: Category Speedometers ── */}
      {speedoCats.length > 0 && (
        <div className="bg-white border border-stone-100 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">Category Pace</p>
          </div>
          <div className="space-y-3">
            {speedoCats.map(cat => {
              const pct = Math.min(150, Math.round((cat.spent / Math.max(1, cat.allocated)) * 100));
              const runway = getCatRunway(cat);
              return (
                <div key={cat.name}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold text-stone-800 truncate max-w-[55%]">{cat.name}</p>
                    <p className={`text-xs font-semibold ${runway.danger ? 'text-red-500' : 'text-emerald-600'}`}>
                      {runway.label}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${getCatBarColor(pct)}`}
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-stone-400 shrink-0 w-10 text-right">{pct}%</span>
                  </div>
                  <p className="text-[10px] text-stone-400 mt-0.5">
                    ₹{fmtAmt(Math.round(cat.spent))} of ₹{fmtAmt(Math.round(cat.allocated))} budget
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Section 3: Pattern Reveals ── */}
      {!hasEnoughData ? (
        <div className="bg-white border border-stone-100 rounded-2xl p-5 shadow-sm text-center">
          <p className="text-sm text-stone-500">Keep tracking for 30 days to unlock patterns.</p>
          <p className="text-xs text-stone-400 mt-1">Add at least 8 transactions this month.</p>
        </div>
      ) : (
        <>
          {showWeekend && (
            <div className="bg-white border border-stone-100 rounded-2xl p-5 shadow-sm space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">Weekend Effect</p>
              </div>
              <p className="text-sm font-semibold text-stone-800">
                You spend{' '}
                <span className="text-orange-500 font-extrabold">{weekendRatio.toFixed(1)}×</span>{' '}
                more on weekends vs weekdays
              </p>
              {/* Bar chart */}
              <div className="flex items-end gap-1.5 h-16">
                {DAY_LABELS.map((label, i) => {
                  const isWeekend = i === 0 || i === 6;
                  const heightPct = (dayAvgs[i] / maxDayAvg) * 100;
                  return (
                    <div key={label} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className={`w-full rounded-t-md transition-all ${isWeekend ? 'bg-orange-400' : 'bg-stone-300'}`}
                        style={{ height: `${Math.max(4, heightPct)}%`, minHeight: 4 }}
                      />
                      <span className={`text-[9px] font-semibold ${isWeekend ? 'text-orange-500' : 'text-stone-400'}`}>
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-stone-400">
                Average spend Mon–Fri: ₹{fmtAmt(Math.round(weekdayAvg))} · Sat–Sun: ₹{fmtAmt(Math.round(weekendAvg))}
              </p>
            </div>
          )}

          {showPaydaySurge && (
            <div className="bg-white border border-stone-100 rounded-2xl p-5 shadow-sm space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">Payday Surge</p>
              </div>
              <p className="text-sm font-bold text-stone-800">
                ₹{fmtAmt(Math.round(earlySpend))} gone in your first 5 days
              </p>
              <p className="text-sm text-stone-500">
                That's <span className="font-bold text-amber-500">{surgePct.toFixed(0)}%</span> of your monthly spend
                in just the first 5 days of the cycle.
              </p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded-full"
                    style={{ width: `${Math.min(100, surgePct)}%` }}
                  />
                </div>
                <span className="text-xs text-stone-400 w-10 text-right">{surgePct.toFixed(0)}%</span>
              </div>
            </div>
          )}

          {showTranslator && (
            <div className="bg-white border border-stone-100 rounded-2xl p-5 shadow-sm space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-violet-500" />
                <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">The Translator</p>
              </div>
              <p className="text-sm font-bold text-stone-800">
                {topCatName} this month = {equivalences[0]}
              </p>
              {equivalences[1] && (
                <p className="text-sm text-stone-500">That's also {equivalences[1]}.</p>
              )}
              <p className="text-xs text-stone-400">
                ₹{fmtAmt(Math.round(topSpend))} spent on {topCatName} so far this cycle
              </p>
            </div>
          )}
        </>
      )}

      {/* ── Section 4: Spend Personality ── */}
      {displayBadges.length > 0 && (
        <div className="bg-white border border-stone-100 rounded-2xl p-5 shadow-sm space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">Your Spend Personality</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {displayBadges.map(badge => (
              <div key={badge.name} className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-2xl px-4 py-3 flex-1 min-w-[140px]">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-xl shrink-0">
                  {badge.icon}
                </div>
                <div>
                  <p className="text-sm font-extrabold text-stone-800">{badge.name}</p>
                  <p className="text-xs text-stone-400 mt-0.5">{badge.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Section 5: Month-End Forecast ── */}
      <div className={`rounded-2xl p-5 shadow-sm border ${
        totalIncome === 0
          ? 'bg-white border-stone-100'
          : forecast >= 0
            ? 'bg-emerald-50 border-emerald-100'
            : 'bg-red-50 border-red-100'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-orange-500" />
          <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">Month-End Forecast</p>
        </div>

        {totalIncome === 0 ? (
          <div>
            <p className="text-sm text-stone-500">Set your income to activate forecast.</p>
            <Link to="/chat" className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-orange-500 hover:text-orange-600">
              Add via Chanakya <ArrowRight size={13} />
            </Link>
          </div>
        ) : forecast >= 0 ? (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-base font-extrabold text-stone-800">
                  💰 Save ₹{fmtAmt(Math.round(forecast))} this month
                </p>
                {forecastDiff !== null && (
                  <p className={`text-xs font-semibold mt-0.5 ${forecastDiff >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {forecastDiff >= 0 ? '↑' : '↓'} ₹{fmtAmt(Math.round(Math.abs(forecastDiff)))} {forecastDiff >= 0 ? 'better' : 'worse'} than last month
                  </p>
                )}
              </div>
            </div>
            <Link
              to={`/chat?q=I'm on pace to save ₹${Math.round(forecast)} this month. How can I push it higher?`}
              className="mt-3 inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-colors"
            >
              Ask Chanakya how to push it higher →
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-base font-extrabold text-red-700">
              On track to overspend by ₹{fmtAmt(Math.round(Math.abs(forecast)))}
            </p>
            <p className="text-xs text-red-500">
              Reduce daily spend by ₹{fmtAmt(Math.round(Math.abs(forecast) / Math.max(1, daysRemaining)))} to break even.
            </p>
            <Link
              to={`/chat?q=I'm on track to overspend by ₹${Math.round(Math.abs(forecast))} this month. What should I cut?`}
              className="mt-3 inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-colors"
            >
              Ask Chanakya what to cut →
            </Link>
          </div>
        )}
      </div>

    </div>
  );
}

// ─── Tool sections (mirrors mobile + Playbook) ────────────────────────────────
const TOOL_SECTIONS = [
  {
    title: 'Invest & Grow', emoji: '📈',
    tools: [
      { id: 'sip',      emoji: '📈', title: 'SIP Calculator',    sub: 'Grow wealth over time',            color: '#10b981', to: '/fire' },
      { id: 'planner',  emoji: '🥧', title: 'Invest ₹X',         sub: 'Enter amount → get allocation',   color: '#059669', to: '/investment-advisor' },
      { id: 'stock',    emoji: '📊', title: 'Stock Analyzer',     sub: "Chanakya's take on any stock",    color: '#3b82f6', to: '/chat' },
      { id: 'gold',     emoji: '🥇', title: 'Gold Tracker',       sub: 'Live gold price & holdings',      color: '#d97706', to: '/gold' },
      { id: 'funds',    emoji: '🧺', title: 'Fund Browser',       sub: 'Browse & compare mutual funds',   color: '#8b5cf6', to: '/investments' },
    ],
  },
  {
    title: 'Safe & Secure', emoji: '🛡️',
    tools: [
      { id: 'ppf',      emoji: '🛡️', title: 'PPF Calculator',    sub: '15-year tax-free compound',       color: '#0d9959', to: '/fire' },
      { id: 'fd',       emoji: '🔒', title: 'FD Calculator',     sub: 'Fixed deposit maturity',          color: '#6366f1', to: '/fire' },
      { id: 'rd',       emoji: '🔄', title: 'RD Calculator',     sub: 'Recurring deposit maturity',      color: '#0891b2', to: '/fire' },
      { id: 'epf',      emoji: '🏛️', title: 'EPF Calculator',    sub: 'Provident fund growth',           color: '#0891b2', to: '/fire' },
    ],
  },
  {
    title: 'Tax & Salary', emoji: '🧾',
    tools: [
      { id: 'salary',   emoji: '💵', title: 'In-Hand Salary',    sub: 'CTC to monthly take-home',        color: '#16b96e', to: '/income' },
      { id: 'tax',      emoji: '📄', title: 'Income Tax',        sub: 'Old vs new regime FY 25-26',      color: '#be123c', to: '/fire' },
    ],
  },
  {
    title: 'Plan & Compare', emoji: '🏠',
    tools: [
      { id: 'emi-calc', emoji: '🧮', title: 'EMI Calculator',    sub: 'What will a new loan cost?',      color: '#6366f1', to: '/emis' },
      { id: 'buy-rent', emoji: '🏠', title: 'Rent vs Buy',       sub: 'Is buying always smarter?',       color: '#3b82f6', to: '/tools/rent-vs-buy-calculator' },
      { id: 'fire',     emoji: '🔥', title: 'FIRE Calculator',   sub: 'When can you retire early?',      color: '#f43f5e', to: '/fire' },
      { id: 'lifetime', emoji: '👛', title: 'Lifetime Earnings', sub: 'How much will you earn?',         color: '#0891b2', to: '/lifetime-earnings' },
      { id: 'cc-trap',  emoji: '💳', title: 'CC Minimum Trap',   sub: 'Real cost of paying just minimum', color: '#ef4444', to: '/tools/credit-card-trap' },
      { id: 'prepay',   emoji: '🏦', title: 'Prepay Home Loan',  sub: 'Save years & lakhs of interest',  color: '#10b981', to: '/tools/home-loan-prepay-calculator' },
    ],
  },
  {
    title: 'Life Events', emoji: '🎯',
    tools: [
      { id: 'home-afford',  emoji: '🏡', title: 'Home Affordability', sub: 'Max home you can afford',          color: '#f97316', to: '/tools/home-affordability-calculator' },
      { id: 'hra',          emoji: '🧾', title: 'HRA Exemption',      sub: 'How much tax your rent saves',     color: '#10b981', to: '/tools/hra-calculator' },
      { id: 'gratuity',     emoji: '🎁', title: 'Gratuity',           sub: 'What you\'ll receive at exit',     color: '#8b5cf6', to: '/tools/gratuity-calculator' },
      { id: 'car-loan',     emoji: '🚗', title: 'Car Loan',           sub: 'EMI, total cost, affordability',   color: '#6366f1', to: '/tools/car-loan-calculator' },
      { id: 'rent-receipt', emoji: '🧾', title: 'Rent Receipt',       sub: 'Generate for HRA claims',          color: '#6366f1', to: '/tools/rent-receipt-generator' },
    ],
  },
  {
    title: 'Family', emoji: '👨‍👩‍👧',
    tools: [
      { id: 'event',    emoji: '🎊', title: 'Event Planner',     sub: 'Wedding, party budgets',          color: '#ec4899', to: '/trips' },
      { id: 'gifts',    emoji: '🎁', title: 'Celebrations',      sub: 'Track gifts & occasions',         color: '#ef4444', to: '/gifts' },
      { id: 'circle',   emoji: '👥', title: 'Family Circle',     sub: 'Shared expenses & settlements',   color: '#f97316', to: '/circle' },
    ],
  },
];

const FILTER_CHIPS = [
  { id: 'all', label: 'All' },
  ...TOOL_SECTIONS.map(s => ({ id: s.title, label: s.title })),
];

// ─── Financial snapshot hook ──────────────────────────────────────────────────
function useFinancialSnapshot() {
  const { token } = useAuth();
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    Promise.allSettled([
      axios.get(`${API}/income-entries/month-summary`, { headers: h }),
      axios.get(`${API}/transactions`, { headers: h, params: { month, year } }),
      axios.get(`${API}/emis`, { headers: h }),
      axios.get(`${API}/investments`, { headers: h }),
    ]).then(([incomeRes, txnRes, emisRes, invRes]) => {
      const income = incomeRes.status === 'fulfilled' ? (incomeRes.value.data?.total_net || 0) : 0;
      const txns = txnRes.status === 'fulfilled' ? (txnRes.value.data || []) : [];
      const expenses = txns.filter(t => t.type === 'expense').reduce((s, t) => s + (t.amount || 0), 0);
      const emis = emisRes.status === 'fulfilled' ? (emisRes.value.data || []) : [];
      const emiTotal = emis.filter(e => e.status === 'active').reduce((s, e) => s + (e.monthly_emi || 0), 0);
      const investments = invRes.status === 'fulfilled' ? (invRes.value.data || []) : [];
      const savings = investments.reduce((s, i) => s + (i.current_value || i.amount || 0), 0);
      const savingsRate = income > 0 ? ((income - expenses) / income) * 100 : 0;
      const emiRatio = income > 0 ? (emiTotal / income) * 100 : 0;
      const emergencyMonths = expenses > 0 ? savings / expenses : 0;
      setSnap({ income, expenses, emiTotal, savings, savingsRate, emiRatio, emergencyMonths, loaded: true });
    });
  }, [token]);

  return snap;
}

// ─── Actions generator ────────────────────────────────────────────────────────
function getActions(p) {
  if (!p || !p.loaded || p.income === 0) return [];
  const actions = [];

  if (p.emergencyMonths < 3) {
    const gap = Math.max(0, p.expenses * 6 - p.savings);
    actions.push({ emoji: '🚨', level: 'urgent', color: '#ef4444',
      trend: `Emergency fund: ${p.emergencyMonths.toFixed(1)} months`,
      action: 'Build at least 6 months of expenses as an emergency fund.',
      detail: `Gap: ${fmtAmt(gap)}. A ₹${fmtAmt(Math.round(gap / 12))}/mo RD closes this in a year.`,
      to: '/fire' });
  }

  if (p.emiRatio > 40) {
    actions.push({ emoji: '💳', level: 'urgent', color: '#ef4444',
      trend: `EMI burden: ${Math.round(p.emiRatio)}% of income`,
      action: `Safe limit is 40%. You're at ${Math.round(p.emiRatio)}%.`,
      detail: 'Prepay your highest-rate loan first. Extra payment this month reduces total interest.',
      to: '/emis' });
  } else if (p.emiTotal > 0) {
    actions.push({ emoji: '✅', level: 'opportunity', color: '#10b981',
      trend: `EMI burden: ${Math.round(p.emiRatio)}% — within safe limits`,
      action: 'Your loan payments are manageable.',
      detail: 'Consider prepaying to save on interest, or invest the surplus instead.',
      to: '/emis' });
  }

  if (p.savingsRate < 10) {
    actions.push({ emoji: '📉', level: 'important', color: '#f59e0b',
      trend: `Savings rate: ${Math.round(p.savingsRate)}%`,
      action: `You need at least 20% to build wealth. You're at ${Math.round(p.savingsRate)}%.`,
      detail: `Start with the 50/30/20 rule. Saving ₹${fmtAmt(Math.round(p.income * 0.05))} more per month makes a difference.`,
      to: '/fire' });
  } else if (p.savingsRate < 20) {
    actions.push({ emoji: '⏳', level: 'important', color: '#f59e0b',
      trend: `Savings rate: ${Math.round(p.savingsRate)}% — building`,
      action: `Push from ${Math.round(p.savingsRate)}% to 20%.`,
      detail: 'Review your top spending category and cut 10-15% from it.',
      to: '/budget' });
  } else {
    actions.push({ emoji: '🚀', level: 'opportunity', color: '#10b981',
      trend: `Savings rate: ${Math.round(p.savingsRate)}% — strong`,
      action: 'You\'re saving more than 20%. Time to invest the surplus.',
      detail: 'Start or increase a SIP in an index fund. Nifty 50 index funds compound at ~12% long term.',
      to: '/investments' });
  }

  if (p.savings === 0) {
    actions.push({ emoji: '📈', level: 'important', color: '#f59e0b',
      trend: 'No investments tracked yet',
      action: 'Start small. ₹500/month in a SIP grows to ₹7L+ in 20 years.',
      detail: 'Add your FDs, mutual funds, PPF, or stocks in the Investments section.',
      to: '/investments' });
  }

  return actions;
}

// ─── Scorecard ────────────────────────────────────────────────────────────────
function Scorecard({ p }) {
  const [open, setOpen] = useState(false);
  if (!p?.loaded || p.income === 0) return null;

  const metrics = [
    { label: 'Savings Rate', value: `${Math.round(p.savingsRate)}%`, stars: p.savingsRate >= 30 ? 5 : p.savingsRate >= 20 ? 4 : p.savingsRate >= 10 ? 3 : p.savingsRate > 0 ? 2 : 1, color: p.savingsRate >= 20 ? '#10b981' : p.savingsRate >= 10 ? '#f59e0b' : '#ef4444' },
    { label: 'EMI Burden',   value: `${Math.round(p.emiRatio)}%`,   stars: p.emiRatio === 0 ? 5 : p.emiRatio <= 20 ? 4 : p.emiRatio <= 35 ? 3 : p.emiRatio <= 50 ? 2 : 1, color: p.emiRatio <= 20 ? '#10b981' : p.emiRatio <= 35 ? '#f59e0b' : '#ef4444' },
    { label: 'Emergency',    value: p.emergencyMonths >= 3 ? '3+ mo' : p.savings > 0 ? `${p.emergencyMonths.toFixed(1)} mo` : 'None', stars: p.emergencyMonths >= 3 ? 5 : p.savings > 0 ? 2 : 1, color: p.emergencyMonths >= 3 ? '#10b981' : p.savings > 0 ? '#f59e0b' : '#ef4444' },
    { label: 'Investments',  value: p.savings > 0 ? fmtAmt(p.savings) : 'None', stars: p.savings > p.income * 6 ? 4 : p.savings > 0 ? 2 : 1, color: p.savings > p.income * 3 ? '#10b981' : '#f59e0b' },
    { label: 'Budget',       value: `${Math.round((p.expenses / p.income) * 100)}%`, stars: p.expenses <= p.income * 0.7 ? 4 : p.expenses <= p.income ? 2 : 1, color: p.expenses <= p.income * 0.7 ? '#10b981' : '#f59e0b' },
  ];
  const overall = Math.round(metrics.reduce((s, m) => s + m.stars, 0) / metrics.length);
  const label = overall >= 4 ? 'All-rounder' : overall >= 3 ? 'Solid' : overall >= 2 ? 'Work in progress' : 'Needs coaching';
  const color = overall >= 4 ? '#10b981' : overall >= 3 ? '#f59e0b' : '#ef4444';

  return (
    <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-sm mb-4">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 dark:hover:bg-stone-800/50 rounded-2xl transition-colors">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ backgroundColor: color }}>{overall}/5</div>
        <div className="flex-1">
          <p className="text-sm font-bold text-stone-800 dark:text-stone-100">{label}</p>
          <p className="text-[11px] text-stone-400">Tap to see full scorecard</p>
        </div>
        <div className="text-sm">{'⭐'.repeat(overall)}{'☆'.repeat(5 - overall)}</div>
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {metrics.map(m => (
            <div key={m.label} className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-3">
              <p className="text-[10px] text-stone-400 uppercase tracking-wide mb-1">{m.label}</p>
              <p className="text-base font-bold" style={{ color: m.color }}>{m.value}</p>
              <p className="text-[10px] text-stone-400 mt-0.5">{'★'.repeat(m.stars)}{'☆'.repeat(5 - m.stars)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function InsightsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('tools');
  const [toolFilter, setToolFilter] = useState('all');
  const snap = useFinancialSnapshot();

  const TABS = [
    { id: 'tools',   label: 'Tools',   icon: Calculator },
    { id: 'trends',  label: 'Trends',  icon: TrendingUp },
    { id: 'actions', label: 'Actions', icon: Zap },
  ];

  const visibleTools = toolFilter === 'all'
    ? TOOL_SECTIONS.flatMap(s => s.tools)
    : (TOOL_SECTIONS.find(s => s.title === toolFilter)?.tools || []);

  const actions = getActions(snap);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">

      {/* Sticky header + tab bar */}
      <div className="sticky top-0 z-20 bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="px-4 pt-4 pb-0">
          <h1 className="text-xl font-extrabold text-stone-900 dark:text-stone-100">Insights</h1>
          <p className="text-xs text-stone-400 mt-0.5">Your money · actions · trends <span className="text-[10px] text-stone-300 dark:text-stone-600">· AI powered</span></p>

          {/* Segmented control */}
          <div className="flex bg-stone-100 dark:bg-stone-800 rounded-2xl p-1 mt-3 mb-0.5 gap-1">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-bold transition-all duration-150
                    ${active ? 'bg-orange-500 text-white shadow-sm' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`}>
                  <Icon size={13} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Category filter — only on Tools tab */}
        {tab === 'tools' && (
          <div className="flex gap-2 overflow-x-auto px-4 py-2 scrollbar-hide">
            {FILTER_CHIPS.map(chip => (
              <button key={chip.id} onClick={() => setToolFilter(chip.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                  ${toolFilter === chip.id ? 'bg-orange-500 text-white' : 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'}`}>
                {chip.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">

        {/* ── TOOLS TAB ── */}
        {tab === 'tools' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {visibleTools.map(tool => (
                <button key={tool.id} onClick={() => navigate(tool.to)}
                  className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl p-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col gap-2"
                  style={{ borderColor: tool.color + '30' }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                    style={{ backgroundColor: tool.color + '15' }}>
                    {tool.emoji}
                  </div>
                  <p className="text-sm font-extrabold text-stone-900 dark:text-stone-100">{tool.title}</p>
                  <p className="text-xs text-stone-400 leading-relaxed flex-1">{tool.sub}</p>
                  <div className="flex justify-end">
                    <ArrowRight size={13} style={{ color: tool.color }} />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── TRENDS TAB ── */}
        {tab === 'trends' && (
          <LiveInsightsTrends snap={snap} />
        )}

        {/* ── ACTIONS TAB ── */}
        {tab === 'actions' && (
          <>
            {!snap ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-2">
                  <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-xs text-stone-400">Loading your financial snapshot…</p>
                </div>
              </div>
            ) : snap.income === 0 ? (
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-2xl p-5">
                <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Add your income to get personalised actions</p>
                <p className="text-xs text-amber-700/70 dark:text-amber-400/70 mt-1 mb-3">
                  We need your monthly income to calculate EMI burden, savings rate, and emergency fund status.
                </p>
                <button onClick={() => navigate('/income')}
                  className="text-sm font-bold text-orange-500 flex items-center gap-1 hover:text-orange-600">
                  Add income <ArrowRight size={13} />
                </button>
              </div>
            ) : (
              <>
                {/* Scorecard */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    <p className="text-[11px] font-bold text-stone-400 uppercase tracking-widest">NOW</p>
                    <p className="text-[11px] text-stone-400">Your snapshot</p>
                  </div>
                  <Scorecard p={snap} />
                </div>

                {/* Action cards */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-violet-500" />
                    <p className="text-[11px] font-bold text-stone-400 uppercase tracking-widest">YOUR ACTIONS</p>
                    <p className="text-[11px] text-stone-400">{actions.length} personalised</p>
                  </div>
                  <div className="space-y-3">
                    {actions.map((a, i) => (
                      <div key={i} className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-4"
                        style={{ borderLeftWidth: 3, borderLeftColor: a.color }}>
                        <div className="flex items-start gap-3">
                          <span className="text-xl shrink-0">{a.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: a.color + '20', color: a.color }}>
                                {a.level === 'urgent' ? 'URGENT' : a.level === 'important' ? 'IMPORTANT' : 'OPPORTUNITY'}
                              </span>
                            </div>
                            <p className="text-xs font-semibold text-stone-500 dark:text-stone-400 mb-1">{a.trend}</p>
                            <p className="text-sm font-bold text-stone-800 dark:text-stone-100 mb-1">{a.action}</p>
                            <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed mb-3">{a.detail}</p>
                            <button onClick={() => navigate(a.to)}
                              className="text-xs font-bold flex items-center gap-1 hover:underline"
                              style={{ color: a.color }}>
                              Take action <ArrowRight size={11} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}
